import { t } from "@lingui/core/macro";
import { useCallback } from "react";

import { Patron } from "@/features/AddPatrons/types";

import { useUpdateDiningBookingData } from "../../../../../hooks/useUpdateDiningBookingData";
import type { DiningAttendeesModal_booking$data } from "../__generated__/DiningAttendeesModal_booking.graphql";
import type { Attendee } from "../components/types";
import { buildDisplayName } from "../utils/attendeeUtils";

type Mode =
  | { type: "list" }
  | { type: "add" }
  | { type: "replace"; targetPlayerId: string };

type UseAttendeeHandlersParams = {
  attendees: Attendee[];
  ownerId: string | null;
  mode: Mode;
  maxPartySize: number | null;
  setAttendees: React.Dispatch<React.SetStateAction<Attendee[]>>;
  setOwnerId: React.Dispatch<React.SetStateAction<string | null>>;
  setMode: React.Dispatch<React.SetStateAction<Mode>>;
  setSearch: (search: string) => void;
};

/**
 * Creates an Attendee from a Patron
 */
export const createAttendeeFromPatron = (patron: Patron): Attendee | null => {
  if (patron.type !== "Member" && patron.type !== "Guest") {
    return null;
  }

  const playerId = patron.playerId ?? null;
  if (!playerId) {
    return null;
  }

  const playerType = patron.type === "Member" ? "Member" : "Guest";

  return {
    playerId,
    displayName: buildDisplayName({
      firstName: patron.firstName,
      lastName: patron.lastName,
    }),
    firstName: patron.firstName ?? null,
    lastName: patron.lastName ?? null,
    profileImagePublicId: patron.profileImageId ?? null,
    playerType,
  };
};

/**
 * Hook for attendee manipulation handlers
 */
export const useAttendeeHandlers = ({
  attendees,
  ownerId,
  mode,
  maxPartySize,
  setAttendees,
  setOwnerId,
  setMode,
  setSearch,
}: UseAttendeeHandlersParams) => {
  const handleToggleAttendee = useCallback(
    (patron: Patron, checked: boolean) => {
      const candidate = createAttendeeFromPatron(patron);
      if (!candidate) {
        return;
      }

      if (mode.type === "replace") {
        if (!checked) {
          return;
        }
        if (mode.targetPlayerId === ownerId) {
          setMode({ type: "list" });
          setSearch("");
          return;
        }
        setAttendees((prev) => {
          const withoutTarget = prev.filter(
            (attendee) => attendee.playerId !== mode.targetPlayerId
          );
          const withoutCandidate = withoutTarget.filter(
            (attendee) => attendee.playerId !== candidate.playerId
          );
          const updated = [...withoutCandidate, candidate];

          return updated;
        });
        setMode({ type: "list" });
        setSearch("");
        return;
      }

      if (checked) {
        let didAddCandidate = false;
        setAttendees((prev) => {
          if (
            prev.some((attendee) => attendee.playerId === candidate.playerId)
          ) {
            return prev;
          }
          if (maxPartySize != null && prev.length >= maxPartySize) {
            return prev;
          }
          didAddCandidate = true;
          return [...prev, candidate];
        });
        if (didAddCandidate) {
          setOwnerId(
            (previousOwnerId) => previousOwnerId ?? candidate.playerId
          );
          if (mode.type === "add") {
            setMode({ type: "list" });
            setSearch("");
          }
        }
      } else {
        setAttendees((prev) => {
          if (candidate.playerId === ownerId) {
            return prev;
          }
          if (prev.length <= 1) {
            return prev;
          }
          const updated = prev.filter(
            (attendee) => attendee.playerId !== candidate.playerId
          );
          return updated;
        });
      }
    },
    [maxPartySize, mode, ownerId, setAttendees, setMode, setOwnerId, setSearch]
  );

  const handleRemoveAttendee = useCallback(
    (playerId: string) => {
      setAttendees((prev) => {
        if (playerId === ownerId) {
          return prev;
        }
        if (prev.length <= 1) {
          return prev;
        }
        const updated = prev.filter(
          (attendee) => attendee.playerId !== playerId
        );
        return updated;
      });
    },
    [ownerId, setAttendees]
  );

  return {
    handleToggleAttendee,
    handleRemoveAttendee,
  };
};

/**
 * Hook for save handler
 */
type UseSaveHandlerParams = {
  booking: DiningAttendeesModal_booking$data | null;
  attendees: Attendee[];
  ownerId: string | null;
  diningReservationHoldId: string | null;
  onClose: () => void;
};

export const useSaveHandler = ({
  booking,
  attendees,
  ownerId,
  diningReservationHoldId,
  onClose,
}: UseSaveHandlerParams) => {
  const [updateDiningBooking, isUpdating] = useUpdateDiningBookingData();
  const handleSave = useCallback(() => {
    if (!booking?.id || !ownerId) {
      return;
    }

    const otherPatronIds = attendees
      .filter((attendee) => attendee.playerId !== ownerId)
      .map((attendee) => attendee.playerId);

    updateDiningBooking({
      input: {
        bookingId: booking.id,
        ownerId,
        otherPatronIds,
        dietaryTags: booking.dietaryTags ?? [],
        occasionTags: booking.occasionTags ?? [],
        notes: booking.notesFromMember ?? "",
        ...(diningReservationHoldId && booking.status === "DRAFT"
          ? { reservationHoldId: diningReservationHoldId }
          : {}),
      },
      onCompleted: () => {
        onClose();
      },
      successToastMessage: t`Attendees updated`,
    });
  }, [
    attendees,
    booking?.dietaryTags,
    booking?.id,
    booking?.notesFromMember,
    booking?.occasionTags,
    diningReservationHoldId,
    booking?.status,
    onClose,
    ownerId,
    updateDiningBooking,
  ]);

  return [handleSave, isUpdating] as const;
};

/**
 * Hook for mode handlers
 */
type UseModeHandlersParams = {
  setMode: React.Dispatch<React.SetStateAction<Mode>>;
  setSearch: (search: string) => void;
};

export const useModeHandlers = ({
  setMode,
  setSearch,
}: UseModeHandlersParams) => {
  const handleBackToList = useCallback(() => {
    setMode({ type: "list" });
    setSearch("");
  }, [setMode, setSearch]);

  const handleOpenAdd = useCallback(() => {
    setMode({ type: "add" });
  }, [setMode]);

  const handleReplaceRequest = useCallback(
    (playerId: string) => {
      setMode({ type: "replace", targetPlayerId: playerId });
    },
    [setMode]
  );

  return {
    handleBackToList,
    handleOpenAdd,
    handleReplaceRequest,
  };
};
